import { isArray, uniqBy } from 'lodash';

/**
 * Helper utilities for Design Detail module
 * Contains data transformation and utility functions
 */

const dataTextHtml = 'data:text/html';

/**
 * Prepare designs array for save operation
 * Normalizes approveBy and createdBy fields from objects to IDs
 * Filters out temporary entries with isNew flag
 *
 * @param {Array} data - Current data array
 * @param {Array} designDocList - Design document list
 * @returns {Array} - Normalized designs array
 */
export const prepareDesignsForSave = (data, designDocList, openArchive) => {
  let array = [];

  if (isArray(data) && isArray(designDocList)) {
    array = uniqBy([...data, ...designDocList], '_id');
  }

  const filteredArray = array?.filter((ele) => !ele?.isNew && (openArchive ? ele?.isArchived : !ele?.isArchived)) || [];

  return filteredArray?.map((ele) => {
    const normalized = { ...ele };

    if (typeof ele?.approveBy === 'object') {
      normalized.approveBy = ele?.approveBy?._id;
    }

    if (typeof ele?.createdBy === 'object') {
      normalized.createdBy = ele?.createdBy?._id;
    }

    return normalized;
  });
};

/**
 * Get class name for image slot column count
 * @param {number} numberOfImages - Number of images (1-6)
 * @returns {string} - CSS class name
 */
export const getColumnClassName = (numberOfImages) => {
  switch (numberOfImages) {
    case 1:
      return 'slot-column-1';
    case 2:
      return 'slot-column-2';
    case 3:
      return 'slot-column-3';
    case 4:
      return 'slot-column-4';
    case 6:
      return 'slot-column-6';
    default:
      return 'slot-column-1';
  }
};

/**
 * Filter designs by archive status
 * @param {Array} designs - Designs array
 * @param {boolean} showArchived - Whether to show archived or active designs
 * @returns {Array} - Filtered designs
 */
export const filterDesignsByArchiveStatus = (designs, showArchived) => {
  if (!isArray(designs) || !designs.length) {
    return [];
  }

  return showArchived ? designs.filter((design) => design.isArchived) : designs.filter((design) => !design.isArchived);
};

/**
 * Get image source path with fallback
 * @param {Object} element - Design element
 * @param {number} idx - Image index
 * @param {string} imageUrl - Base image URL
 * @returns {string|null} - Image source URL or null
 */
export const getImageSource = (element, idx, imageUrl) => {
  const slot = element?.images?.[idx];

  if (!slot) return null;

  const srcPath = slot?.annotedImage && slot?.annotedImage !== '' ? slot.annotedImage : slot?.image;

  if (!srcPath) return null;

  const isBase64 = srcPath.startsWith('data:image');

  return isBase64 ? srcPath : `${imageUrl}/${srcPath}`;
};

/**
 * Check if string is base64 image
 * @param {string} str - String to check
 * @returns {boolean} - True if base64 image
 */
export const isBase64Image = (str) => {
  return typeof str === 'string' && str.startsWith('data:image');
};

/**
 * Download file helper
 * @param {string} url - File URL
 * @param {string} fileName - File name
 * @param {string} extension - File extension
 */
export const downloadFileHelper = (url, fileName, extension) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = `${fileName}.${extension}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Format file size in MB
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted size string
 */
export const formatFileSize = (bytes) => {
  const mb = bytes / 1024 / 1024;
  return `${mb.toFixed(2)}MB`;
};

export const DATA_TEXT_HTML = dataTextHtml;
export const MAX_IMAGE_SIZE_MB = 20;

export const generateUniqueFilePrefix = () => {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  const randomBytes = new Uint8Array(16);
  window.crypto.getRandomValues(randomBytes);
  return Array.from(randomBytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
};
export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
export const ALLOWED_DOCUMENT_TYPES = {
  pdf: ['application/pdf'],
  ai: ['application/postscript', 'application/illustrator'],
};
